/**
 * @fileoverview Frontend API Client V2 (Advanced Type Inference)
 * @module @monorepo/api-client/frontend
 *
 * Next-generation frontend client with automatic type inference from registry.
 * No manual type definitions needed - everything is inferred!
 *
 * Features:
 * - Automatic type inference from client registry
 * - Direct property access to all clients
 * - Full autocomplete support
 * - Type-safe method calls
 */

import { ExampleClient } from '../clients/example.client';
import { UsersClient } from '../clients/users.client';
import { SessionClient } from '../clients/session.client';
import { TestSessionClient } from '../clients/test-session.client';
import { TrackingClient } from '../clients/tracking.client';
import { PaymentClient } from '../clients/payment.client';
import { createProxyApiClient, type ApiClientConfig } from '../core/api-client-builder';

// ============================================================================
// Frontend Client Registry
// ============================================================================

/**
 * Frontend client registry
 * Add new clients here and they'll automatically be available with full types!
 *
 * Frontend has access to public and user-facing endpoints only.
 * Admin-only endpoints are excluded for security and bundle size optimization.
 *
 * TODO: Create additional client classes as needed:
 * - auth: AuthClient (for login/logout)
 * - session: SessionClient (for CSRF tokens)
 * - payment: PaymentClient (for Stripe integration)
 * - testResults: TestResultClient (for test management)
 * - analysis: AnalysisClient (for AI analysis)
 * - etc.
 */
const FRONTEND_CLIENT_REGISTRY = {
	// User Management
	users: UsersClient,

	// Content & Products
	products: ExampleClient,

	// Session Management
	session: SessionClient,

	// Test Session Management (Ulrich LearnType)
	testSession: TestSessionClient,

	// Analytics & Tracking
	tracking: TrackingClient,

	// Payment Processing
	payment: PaymentClient,
} as const;

// ============================================================================
// Type Inference
// ============================================================================

/**
 * Inferred frontend client type
 * TypeScript automatically knows all available clients and their methods!
 */
export type FrontendClientV2 = ReturnType<typeof createFrontendClient>;

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Frontend client configuration
 */
export type FrontendClientV2Config = Omit<ApiClientConfig, 'clientId'>;

/**
 * Create frontend API client with automatic type inference
 *
 * @example
 * ```typescript
 * const frontend = createFrontendClient({
 *   baseURL: 'http://localhost:3000/api',
 *   timeout: 30000,
 * });
 *
 * // All clients are directly accessible with full types!
 *
 * // Session & Auth
 * const { csrfToken } = await frontend.session.verify();
 * await frontend.auth.logout();
 *
 * // Products
 * await frontend.products.list({ page: 1, limit: 10 });
 * await frontend.products.getOne(123);
 *
 * // Payment
 * const intent = await frontend.payment.createIntent({
 *   testId: 'test-123',
 *   customerEmail: 'user@example.com',
 *   analysisType: 'premium',
 * });
 *
 * // Test Results & Analysis
 * const testResult = await frontend.testResults.create({
 *   testType: 'personality',
 *   answers: { q1: 'a', q2: 'b' },
 * });
 * const analysis = await frontend.analysis.generateSingle({
 *   testId: testResult.id,
 *   analysisType: 'basic',
 * });
 *
 * // Tracking
 * await frontend.tracking.trackEvent({
 *   eventType: 'page_view',
 *   eventData: { page: '/products' },
 * });
 *
 * // Access HTTP client and metrics
 * const httpClient = frontend.$http;
 * const metrics = frontend.$metrics();
 * ```
 */
export function createFrontendClient(config: FrontendClientV2Config) {
	return createProxyApiClient(FRONTEND_CLIENT_REGISTRY, {
		...config,
		clientId: 'web',
	});
}

// ============================================================================
// Re-export for convenience
// ============================================================================

export { FRONTEND_CLIENT_REGISTRY };
